import React, { useMemo, useRef, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Btn, Chip, Label, Notice } from "./ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useTrackers } from "../lib/useTrackers.js";
import { useSchedule } from "../lib/schedule/useSchedule.js";
import { fillDeck, readDeck } from "../lib/exporters/deck.js";
import { download, exportName } from "../lib/exporters/download.js";
import { readTemplate, planDeck, trackerCandidates, assetFromTitle } from "../lib/reportDeck.js";
import { fmtDate } from "../lib/format.js";

/* The monthly deck, in the client's own template, from the same report the
 * page below draws.
 *
 * The template is uploaded once per contract and kept in the contract's
 * private storage. Every download reads it fresh and fills it from the rows as
 * they stand, so there is no generated deck sitting anywhere going stale.
 *
 * Two slides read trackers rather than registers, and which tracker is a
 * person's call: this proposes one by name and says so, and nothing is filled
 * from a tracker nobody confirmed when two could fit.
 */

const BUCKET = "contract-documents";
const PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const PAGE = 1000;

const safeName = (name) => String(name).normalize("NFKD")
  .replace(/[^\w.-]+/g, "-").replace(/-+/g, "-").slice(-80);

async function trackerRows(trackerId) {
  const all = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase.from("tracker_rows")
      .select("row_index, data").eq("tracker_id", trackerId).is("deleted_at", null)
      .order("row_index").range(from, from + PAGE - 1);
    if (error) throw new Error(friendlyError(error));
    all.push(...(data ?? []));
    if (!data || data.length < PAGE) return all;
  }
}

const select = {
  padding: "5px 9px", border: `1px solid ${T.line}`, borderRadius: 4,
  fontSize: 13, background: "#fff", color: T.ink, maxWidth: "100%",
};

/* One slide's tracker: the saved choice, else the only candidate, else none
   and a question. */
function TrackerPick({ label, slideNote, trackers, candidates, pick, onChoose, canEdit }) {
  const chosen = pick ? pick.id : undefined;
  const auto = !pick && candidates.length === 1 ? candidates[0] : null;
  const current = chosen ? trackers.find((t) => t.id === chosen) : auto;
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <Label>{label}</Label>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={current?.id ?? ""} disabled={!canEdit}
          onChange={(e) => onChoose(e.target.value || null)} style={select}>
          <option value="">No tracker, leave this slide for me</option>
          {trackers.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
        {auto && <Chip color={GOLD_INK}>Matched by name</Chip>}
        {pick && <Chip color={T.steel}>Chosen</Chip>}
      </div>
      <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>
        {current
          ? slideNote(current)
          : pick
            ? "You chose no tracker here, so this slide is left for you."
            : candidates.length > 1
            ? `${candidates.map((t) => t.title).join(" and ")} could both fit. Choose one and it is used from then on.`
            : "No tracker's name fits this slide, so it is left for you."}
      </div>
    </div>
  );
}

export default function DeckPanel({ contract, report, data }) {
  const { canEdit, profile } = useAuth();
  const { trackers } = useTrackers(contract?.id);
  const schedule = useSchedule(contract?.id);
  const fileRef = useRef(null);

  /* Kept here as well as on the contract, so an upload or a choice shows at
     once rather than after the contract list next reloads. */
  const [saved, setSaved] = useState(() => contract?.report_template ?? {});
  const deck = saved?.deck ?? null;
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const candidates = useMemo(() => trackerCandidates(trackers ?? []), [trackers]);
  const picks = deck?.trackers ?? {};
  /* A saved choice wins, including a saved choice of none. */
  const pickFor = (role) => {
    if (picks[role]) return (trackers ?? []).find((t) => t.id === picks[role].id) ?? null;
    return candidates[role]?.length === 1 ? candidates[role][0] : null;
  };

  const save = async (next) => {
    const { error: failed } = await supabase.from("contracts")
      .update({ report_template: next }).eq("id", contract.id);
    if (failed) { setError(friendlyError(failed)); return false; }
    setSaved(next);
    return true;
  };

  const upload = async (file) => {
    if (!file) return;
    if (!/\.pptx$/i.test(file.name)) {
      setError("That is not a .pptx file. Save the template from PowerPoint as .pptx and try again.");
      return;
    }
    setBusy("Uploading the template...");
    setError("");
    try {
      /* Read before it is kept, so a file that is not a deck is refused here
         rather than on the first month somebody needs it. */
      const shape = readTemplate(readDeck(await file.arrayBuffer()));
      if (!Object.keys(shape).length) {
        throw new Error("None of the deck's slides could be recognised in that file. It may not be the monthly report template.");
      }
      const path = `${contract.id}/report-template/${safeName(file.name)}`;
      const up = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true, contentType: PPTX });
      if (up.error) throw new Error(friendlyError(up.error));
      await save({
        ...saved,
        deck: {
          ...(deck ?? {}),
          path, name: file.name,
          uploaded_at: new Date().toISOString(),
          uploaded_by: profile?.full_name || profile?.email || null,
        },
      });
      setResult(null);
    } catch (e) {
      setError(e.message || String(e));
    }
    setBusy("");
  };

  const choose = (role, id) => {
    const tracker = (trackers ?? []).find((t) => t.id === id);
    const nextPicks = { ...picks };
    if (id) nextPicks[role] = { id, ...(role === "install" ? { asset: assetFromTitle(tracker?.title, contract) } : {}) };
    else nextPicks[role] = { id: null };
    save({ ...saved, deck: { ...deck, trackers: nextPicks } });
  };

  const setAsset = (asset) => {
    const install = pickFor("install");
    if (!install) return;
    save({ ...saved, deck: { ...deck, trackers: { ...picks, install: { ...(picks.install ?? {}), id: install.id, asset } } } });
  };

  const build = async () => {
    if (!deck?.path) return;
    setBusy("Building the deck...");
    setError("");
    setResult(null);
    try {
      const got = await supabase.storage.from(BUCKET).download(deck.path);
      if (got.error) throw new Error(friendlyError(got.error));
      const bytes = await got.data.arrayBuffer();

      const install = pickFor("install");
      const fm = pickFor("fm");
      const [installRows, fmRows] = await Promise.all([
        install ? trackerRows(install.id) : null,
        fm ? trackerRows(fm.id) : null,
      ]);

      const template = readTemplate(readDeck(bytes));
      const { plan, notes } = planDeck({
        report, contract, template,
        extras: {
          risks: data?.risks ?? [],
          issues: data?.issues ?? [],
          schedule: schedule.computed?.activities ?? [],
          install: install ? { tracker: install, rows: installRows, asset: picks.install?.asset } : null,
          fm: fm ? { tracker: fm, rows: fmRows } : null,
        },
      });
      const filled = fillDeck(bytes, plan);
      const name = exportName(contract, `Monthly-Report-${report.month}${filled.draft ? "-DRAFT" : ""}`, "pptx");
      download(filled.bytes, name, PPTX);
      setResult({ ...filled, notes, name, titles: titlesOf(bytes) });
    } catch (e) {
      setError(e.message || String(e));
    }
    setBusy("");
  };

  const install = pickFor("install");

  return (
    <Panel title={`PowerPoint deck · ${report.label}`}
      right={deck?.path ? <Chip color={T.steel}>Template on file</Chip> : <Chip color={GOLD_INK}>No template yet</Chip>}>
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.7 }}>
          The deck is your own template with the numbers filled in from this
          report: the month on every slide, the status table, installation
          progress, the deliverables tracker, what is overdue, the key risks and
          issues, the schedule overview and both invoice aging slides. A long
          table continues onto another slide rather than being cut. Anything a
          person writes is left as the template's placeholder, and while any is
          left the cover says DRAFT and so does the file name.
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {deck?.path && (
            <div style={{ fontSize: 13, color: T.ink }}>
              <strong>{deck.name}</strong>
              <span style={{ color: T.soft }}>
                {" "}· uploaded {fmtDate(String(deck.uploaded_at ?? "").slice(0, 10))}
                {deck.uploaded_by ? ` by ${deck.uploaded_by}` : ""}
              </span>
            </div>
          )}
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".pptx" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
              <Btn kind="ghost" small disabled={Boolean(busy)} onClick={() => fileRef.current?.click()}>
                {deck?.path ? "Replace template" : "Upload template (.pptx)"}
              </Btn>
            </>
          )}
        </div>

        {deck?.path && (
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <TrackerPick label="Installation progress reads" trackers={trackers ?? []}
              candidates={candidates.install} pick={picks.install} canEdit={canEdit}
              onChoose={(id) => choose("install", id)}
              slideNote={(t) => `Counts a row as installed when its completion date falls on or before the end of ${report.label}. Rows marked out of scope are left out.`} />
            <TrackerPick label="Functional module testing reads" trackers={trackers ?? []}
              candidates={candidates.fm} pick={picks.fm} canEdit={canEdit}
              onChoose={(id) => choose("fm", id)}
              slideNote={() => "One row per module, with TID, Build & UT, SIT and status columns."} />
            {install && (
              <div style={{ display: "grid", gap: 4 }}>
                <Label>Installed asset, as the slide title says it</Label>
                <input defaultValue={picks.install?.asset ?? assetFromTitle(install.title, contract)}
                  key={install.id} disabled={!canEdit}
                  onBlur={(e) => setAsset(e.target.value.trim())}
                  style={{ ...select, width: 200 }} />
              </div>
            )}
          </div>
        )}

        {deck?.path && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Btn disabled={Boolean(busy) || !schedule.computed} onClick={build}>Download deck</Btn>
            <span style={{ fontSize: 12.5, color: T.soft }}>
              {busy || (!schedule.computed ? "Loading the schedule..." : "Built in your browser. Nothing is saved.")}
            </span>
          </div>
        )}

        {!deck?.path && !canEdit && (
          <Notice>No template has been uploaded for this contract yet. Ask a member of the team to upload it.</Notice>
        )}
        {error && <Notice kind="error">{error}</Notice>}
        {result && <DeckResult result={result} />}
      </div>
    </Panel>
  );
}

/* Each template slide's name for the list below: the first line of words on
   it, which in this template is the title bar. */
function titlesOf(bytes) {
  return Object.fromEntries(readDeck(bytes).map(({ slide, xml }) => {
    const first = /<a:t(?:\s[^>]*)?>([^<]+)<\/a:t>/.exec(xml)?.[1] ?? "";
    return [slide, first.replace(/&amp;/g, "&").replace(/&apos;/g, "'").trim()];
  }));
}

/* What the download still needs from a person, slide by slide, in the order
   the deck shows them. */
function DeckResult({ result }) {
  const { unfilled, notes, name, draft, titles } = result;
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Notice kind={draft ? "warn" : "ok"}>
        {draft
          ? `Downloaded ${name}. It is marked DRAFT on the cover because ${unfilled.length} slide${unfilled.length === 1 ? " still has" : "s still have"} placeholders a person has to fill.`
          : `Downloaded ${name}. Every placeholder is filled.`}
      </Notice>
      {notes.length > 0 && (
        <div>
          <div style={{ marginBottom: 4 }}><Label>Worth knowing</Label></div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: T.ink, lineHeight: 1.6 }}>
            {notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </div>
      )}
      {unfilled.length > 0 && (
        <div>
          <div style={{ marginBottom: 4 }}><Label>Still to fill</Label></div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <tbody>
              {unfilled.map((u) => (
                <tr key={`${u.position}`}>
                  <td style={{ padding: "5px 8px", borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap",
                               verticalAlign: "top", color: T.navy, fontWeight: 700 }}>
                    Slide {u.position}
                  </td>
                  <td style={{ padding: "5px 8px", borderBottom: `1px solid ${T.line}`, verticalAlign: "top",
                               color: T.soft, minWidth: 140 }}>
                    {titles[u.slide] ? `${titles[u.slide]}${u.page > 1 ? " (continued)" : ""}` : ""}
                  </td>
                  <td style={{ padding: "5px 8px", borderBottom: `1px solid ${T.line}`, color: T.ink }}>
                    {u.placeholders.join("  ·  ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
